<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { useTypedIntl } from "@/locales/intl";
import { useInterfaceStore } from "@/stores/interface/interface-store.ts";
import { ELocale } from "@/stores/interface/interface.types";

import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import InputSwitch from "primevue/inputswitch";
import InputText from "primevue/inputtext";
import Slider from "primevue/slider";

const { t, k } = useTypedIntl();

const visible = defineModel<boolean>("visible", { default: true });

const interfaceStore = useInterfaceStore();

const selectedLanguage = ref(interfaceStore.currentLocale);
const isLargeScreen = ref(interfaceStore.isLargeScreen);

const { fontSize } = storeToRefs(interfaceStore);
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{ width: '50rem' }"
    :header="t(k.settings.title)"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    dismissable-mask
    close-on-escape
    :data-test-id="DATA_TEST_IDS.components.settingsModal.component"
    :pt="{
      title: {
        'data-test-id': DATA_TEST_IDS.components.settingsModal.title(),
      },
    }"
    :close-button-props="{
      'data-test-id': DATA_TEST_IDS.components.settingsModal.closeBtn(),
    }"
  >
    <div class="flex flex-col gap-8">
      <div class="flex flex-col">
        <label
          id="language-label"
          for="language"
          class="font-bold text-lg"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.languageLabel()"
        >
          {{ t(k.settings.language) }}
        </label>
        <small
          id="language-help"
          for="language"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.languageHelp()"
        >
          {{ t(k.settings.languageHelp) }}
        </small>
        <Dropdown
          v-model="selectedLanguage"
          :options="[ELocale.FR, ELocale.EN]"
          name="language"
          aria-labelledby="language-label"
          aria-describedby="language-help"
          :placeholder="t(k.settings.language)"
          :option-label="(opt: ELocale) => t(k.settings.locale[opt])"
          class="w-full mt-4"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.languageDropdown()"
          @change="() => interfaceStore.updateLocale(selectedLanguage)"
        />
      </div>

      <div class="flex flex-col">
        <label
          id="fontSize-label"
          for="fontSize"
          class="font-bold text-lg"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.fontSizeLabel()"
        >
          {{ t(k.settings.fontSize) }}
        </label>
        <small
          id="fontSize-help"
          for="fontSize"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.fontSizeHelp()"
        >
          {{ t(k.settings.fontSizeHelp) }}
        </small>
        <InputText
          :value="fontSize"
          readonly
          disabled
          class="w-full mb-3"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.fontSizeValue()"
        />
        <Slider
          v-model="fontSize"
          :min="8"
          :max="24"
          class="w-full"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.fontSizeSlider()"
        />
      </div>

      <div class="flex flex-col">
        <label
          id="largeScreen-label"
          for="largeScreen"
          class="font-bold text-lg"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.largeScreenLabel()"
        >
          {{ t(k.settings.largeScreen) }}
        </label>
        <small
          id="largeScreen-help"
          for="largeScreen"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.largeScreenHelp()"
        >
          {{ t(k.settings.largeScreenHelp) }}
        </small>
        <InputSwitch
          v-model="isLargeScreen"
          name="largeScreen"
          aria-labelledby="largeScreen-label"
          aria-describedby="largeScreen-help"
          class="mt-4"
          :data-test-id="DATA_TEST_IDS.components.settingsModal.largeScreenSwitch()"
          @change="() => interfaceStore.setLargeScreen(isLargeScreen)"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped></style>
